<template>
  <section class="admin-content" id="auth-code-page">
    <Navbar />
    <main class="admin-main">

      <!-- Page Header -->
      <div class="page-header">
        <div class="container">
          <div class="row align-items-center p-t-40 p-b-40">
            <div class="col-md-8 text-white">
              <h2 class="page-title"><i class="fe fe-shield mr-2"></i>Authorization Codes</h2>
              <p class="page-subtitle">Manage secondary care authorization requests</p>
            </div>
            <div class="col-md-4 text-right"
              v-if="user && (user.type == 'provider_employee' || user.type == 'employee')">
              <button class="btn btn-create" @click="showadder = !showadder">
                <i :class="showadder ? 'fe fe-x' : 'fe fe-plus'" class="mr-1"></i>
                {{ showadder ? 'Close Form' : 'New Request' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- New Request Form -->
      <div class="container mt-n3 mb-4" v-if="showadder">
        <div class="card form-card shadow">
          <div class="form-card-header">
            <div>
              <h5 class="mb-0 font-weight-bold text-white">New Authorization Request</h5>
              <p class="mb-0 mt-1" style="opacity:.8; font-size:.85rem;">Fill in the details to request an authorization code</p>
            </div>
            <button class="form-close-btn" type="button" @click="showadder = false">
              <i class="fe fe-x"></i>
            </button>
          </div>

          <div class="card-body px-4 pb-4">
            <form @submit.prevent="AddDisease">

              <!-- Step 1: Facilities -->
              <div class="form-section">
                <div class="form-section-title">
                  <span class="section-number">1</span> Facilities
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6" v-if="user.type == 'employee' || user.type == 'shis'">
                    <label class="form-label">Originating Facility</label>
                    <v-select v-model="provider_id" :reduce="agency_name => agency_name.id" label="agency_name"
                      :options="providers" placeholder="Search originating facility…" />
                  </div>
                  <div class="form-group col-md-6">
                    <label class="form-label">Receiving Facility</label>
                    <v-select v-model="referred_to_facility" label="agency_name" :options="providers"
                      placeholder="Search receiving facility…" />
                  </div>
                </div>
              </div>

              <!-- Step 2: Patient -->
              <div class="form-section">
                <div class="form-section-title">
                  <span class="section-number">2</span> Patient
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label class="form-label">Search Enrollee</label>
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <span class="input-group-text bg-white"><i class="fe fe-search text-muted"></i></span>
                      </div>
                      <input type="text" class="form-control border-left-0" placeholder="ID, First Name, Last Name…"
                        v-model="searchkey" @change="searchIDCard" @keyup.enter="searchIDCard" />
                      <div class="input-group-append">
                        <button type="button" class="btn btn-create btn-sm px-3" @click="searchIDCard"
                          :disabled="isLoading">
                          <i class="fe fe-search mr-1"></i> Search
                        </button>
                      </div>
                    </div>
                    <small class="text-muted mt-1 d-block" v-if="search_result && search_result.length">
                      {{ search_result.length }} enrollee(s) found
                    </small>
                  </div>
                  <div class="form-group col-md-6">
                    <label class="form-label">Select Enrollee <span class="text-danger">*</span></label>
                    <select class="form-control styled-select" v-model="selected_enrollee" required
                      @change="getRecords(selected_enrollee)">
                      <option value="">-- Select from results --</option>
                      <option :value="usr" v-for="usr in search_result" :key="usr.id">
                        {{ usr.user_query }}
                      </option>
                    </select>
                  </div>

                  <div class="form-group col-md-6">
                    <label class="small font-weight-bold text-secondary">
                      Select Service(s) <span class="text-danger">*</span>
                    </label>
                    <div :class="servicesError ? 'is-invalid-select' : ''">
                      <v-select v-model="auth_services" label="description"
                        :reduce="description => description.id"
                        :options="serviceOptions"
                        @input="servicesError = false"
                        multiple />
                    </div>
                    <small v-if="catalogProgramme" class="text-muted d-block">
                      <i class="fe fe-filter mr-1"></i>Services that apply to {{ catalogProgramme }} ({{ serviceOptions.length }}).
                    </small>
                    <small v-if="servicesError" class="text-danger">Please select at least one service</small>
                  </div>

                  <div class="form-group col-md-6">
                    <label class="small font-weight-bold text-secondary">
                      Select Secondary Diagnosis <span class="text-danger">*</span>
                    </label>
                    <div :class="diagnosisError ? 'is-invalid-select' : ''">
                      <v-select v-model="diagnosis_id" label="name"
                        :options="diseases"
                        :filterable="false"
                        @search="onDiagnosisSearch"
                        @input="diagnosisError = false"
                        placeholder="ICD 11 Diagnosis…">
                        <template #option="option">
                          <span class="text-muted mr-1">({{ option.diagnosis_code }})</span>{{ option.name }}
                        </template>
                        <template #selected-option="option">
                          ({{ option.diagnosis_code }}) - {{ option.name }}
                        </template>
                        <template #no-options="{ search }">
                          <span class="text-muted small">{{ search.length >= 4 ? 'No results found' : 'Search ICD 11 Diagnoses…' }}</span>
                        </template>
                      </v-select>
                    </div>
                    <small v-if="diagnosisError" class="text-danger">Please select a diagnosis</small>
                  </div>

                  <div class="form-group col-md-12">
                    <label class="small font-weight-bold text-secondary">Referral Note</label>
                    <textarea rows="3" class="form-control" v-model="comments[0].body" required></textarea>
                  </div>
                </div>

                <!-- Patient Preview -->
                <div class="patient-preview" v-if="userDetails && selected_enrollee && selected_enrollee.full_name">
                  <img :src="`https://claimify-api.hayokmedicare.ng/image/${selected_enrollee.user_image}`"
                    class="preview-avatar" onerror="this.onerror=null; this.src='/assets/img/claimify_logo.png'"
                    alt="Patient" />
                  <div class="flex-grow-1">
                    <strong>{{ selected_enrollee.full_name }}</strong>
                    <div class="d-flex flex-wrap mt-1" style="gap: 16px;">
                      <small class="text-muted"><i class="fe fe-credit-card mr-1"></i>{{ selected_enrollee.id_card_number }}</small>
                      <small class="text-muted"><i class="fe fe-tag mr-1"></i>{{ selected_enrollee.sector }}</small>
                    </div>
                  </div>
                  <span class="badge badge-success">Active</span>
                </div>
              </div>

              <!-- Step 3: Encounter -->
              <div class="form-section" v-if="userDetails && encounters && encounters.length">
                <div class="form-section-title">
                  <span class="section-number">3</span> Encounter
                </div>
                <div class="form-row">
                  <div class="form-group col-md-12">
                    <label class="form-label">Select Encounter</label>
                    <select class="form-control styled-select" v-model="service_summary_id">
                      <option value="">-- Select encounter --</option>
                      <option :value="encounter.id" v-for="encounter in encounters" :key="encounter.id">
                        {{ encounter.encounter_id }}
                        <span v-if="encounter.diagnosis"> — {{ encounter.diagnosis.name }}</span>
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Submit -->
              <div class="d-flex justify-content-end" style="gap: 10px;">
                <button type="button" class="btn btn-cancel" @click="showadder = false">Cancel</button>
                <button type="submit" class="btn btn-create" :disabled="isLoading">
                  <span v-if="isLoading"><i class="mdi mdi-loading mdi-spin mr-1"></i>Submitting…</span>
                  <span v-else><i class="fe fe-send mr-1"></i>Submit Request</span>
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>

      <!-- Filter Card (admin / employee / shis / vetter / verifier) -->
      <div class="container mb-4"
        v-if="user && (isFacility || user.user_role == 1 || user.job_title == 'Claims Vetter' || user.job_title == 'Claims Verifier' || user.job_title == 'Zonal Coordinator' || user.type == 'shis' || user.type == 'employee')">
        <div class="card filter-card shadow-sm">
          <div class="card-body py-3">
            <p class="font-weight-bold mb-3 text-secondary small text-uppercase letter-spacing">
              <i class="fe fe-filter mr-1"></i> Filter Requests
            </p>
            <form @submit.prevent="getCodes(1)">
              <div class="row align-items-end">
                <div class="form-group col-md-3 mb-2" v-if="user.job_title != 'Zonal Coordinator' && !isFacility">
                  <label class="small text-muted mb-1">Facility</label>
                  <v-select v-model="query.provider_id" label="agency_name" :options="providers"
                    placeholder="All facilities" class="v-select-sm" />
                </div>
                <div class="form-group col-md-2 mb-2">
                  <label class="small text-muted mb-1">Status</label>
                  <select class="form-control form-control-sm" v-model="query.status">
                    <option value="">All</option>
                    <option v-for="s in statuses" :key="s" :value="s">{{ s }}</option>
                  </select>
                </div>
                <div class="form-group col-md-2 mb-2">
                  <label class="small text-muted mb-1">Enrollee ID</label>
                  <input type="text" class="form-control form-control-sm" v-model="query.id_card_number"
                    placeholder="ID / Phone…" />
                </div>
                <div class="form-group col-md-2 mb-2">
                  <label class="small text-muted mb-1">Auth Code</label>
                  <input type="text" class="form-control form-control-sm" v-model="query.code_created"
                    placeholder="Code…" />
                </div>
                <div class="form-group col-md-1 mb-2">
                  <label class="small text-muted mb-1">Start Date</label>
                  <input type="date" class="form-control form-control-sm" v-model="query.from" />
                </div>
                <div class="form-group col-md-1 mb-2">
                  <label class="small text-muted mb-1">End Date</label>
                  <input type="date" class="form-control form-control-sm" v-model="query.to" />
                </div>
                <div class="form-group col-md-1 mb-2">
                  <label class="small text-muted mb-1">Rows</label>
                  <select class="form-control form-control-sm" v-model="query.paginate_value">
                    <option>30</option>
                    <option>100</option>
                    <option>500</option>
                    <option>1000</option>
                  </select>
                </div>
              </div>
              <div class="d-flex align-items-center" style="gap: 8px;">
                <button type="submit" class="btn btn-dark btn-sm px-4">
                  <i class="fe fe-filter mr-1"></i> Apply
                </button>
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="clearFilter">
                  <i class="fe fe-x mr-1"></i> Clear
                </button>
                <button type="button" class="btn btn-outline-success btn-sm ml-auto" @click="exportExcel"
                  :disabled="exporting" title="All requests matching the filters, not just this page">
                  <span v-if="exporting"><i class="mdi mdi-loading mdi-spin mr-1"></i> Exporting…</span>
                  <span v-else><i class="fe fe-download mr-1"></i> Export Excel</span>
                </button>
                <button type="button" class="btn btn-outline-primary btn-sm" v-if="user && user.user_role == 1 && !isFacility"
                  @click="openPerformanceReport">
                  <i class="fe fe-bar-chart-2 mr-1"></i> Employee Performance
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Codes Table -->
      <div class="container mb-5">
        <div class="card codes-card shadow-sm">
          <div class="card-header-custom">
            <div class="d-flex align-items-center" style="gap: 10px;">
              <h5 class="mb-0"><i class="fe fe-list mr-2"></i>Authorization Code Requests</h5>
              <span class="badge badge-pill badge-secondary">{{ rows }} total</span>
            </div>
            <div class="view-toggle">
              <button :class="['view-toggle-btn', viewMode === 'list' ? 'active' : '']" @click="viewMode = 'list'"
                title="List view"><i class="fe fe-list"></i></button>
              <button :class="['view-toggle-btn', viewMode === 'grid' ? 'active' : '']" @click="viewMode = 'grid'"
                title="Grid view"><i class="fe fe-grid"></i></button>
            </div>
          </div>

          <!-- List View -->
          <div class="card-body p-0" v-if="viewMode === 'list'">
            <div class="table-responsive">
              <table class="table codes-table mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Enrollee</th>
                    <th>Requesting Facility</th>
                    <th>Expiry</th>
                    <th>Auth Code</th>
                    <th>Status</th>
                    <th v-if="isEmployee" style="width: 130px;">Live</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!codes || !codes.data || !codes.data.length">
                    <td colspan="8" class="text-center empty-state">
                      <i class="fe fe-shield" style="font-size:2rem;opacity:0.3"></i>
                      <p class="mt-2 mb-0">No authorization codes found</p>
                    </td>
                  </tr>
                  <tr v-for="diag in codes.data" :key="diag.id" class="code-row">
                    <td>
                      <span class="d-block">{{ diag.date_requested }}</span>
                      <small :class="`text-${diag.delay && diag.delay.color ? diag.delay.color : 'muted'}`">
                        {{ diag.created_at | moment("from", "now") }}
                      </small>
                    </td>
                    <td>
                      <span v-if="diag.enrollee" class="font-weight-medium">
                        {{ diag.enrollee.full_name }} — {{ diag.enrollee.id_card_number }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td>
                      <span v-if="diag.provider">{{ diag.provider.agency_name }}</span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td>{{ diag.expiry_date || '—' }}</td>
                    <td>
                      <code v-if="diag.code_created" class="code-chip">{{ diag.code_created }}</code>
                      <span v-else class="text-muted small">Pending</span>
                    </td>
                    <td>
                      <span :class="'status-badge ' + (diag.status || 'pending')">{{ diag.status || 'pending' }}</span>
                      <span v-if="diag.is_code_used" class="used-badge ml-1">used</span>
                      <span v-else-if="diag.status == 'approved'" class="text-muted small ml-1">not used</span>
                    </td>
                    <td v-if="isEmployee">
                      <div v-if="isLockActive(diag)" class="live-lock-badge">
                        <div class="live-avatar" :class="{ 'live-avatar-mine': isMyLock(diag) }"
                          :title="isMyLock(diag) ? 'You are viewing this' : diag.locked_by_name + ' is viewing'">
                          {{ nameInitials(diag.locked_by_name) }}
                        </div>
                        <div class="live-info">
                          <span>{{ isMyLock(diag) ? 'You' : diag.locked_by_name }}</span>
                          <small><span class="live-dot" :class="{ 'live-dot-mine': isMyLock(diag) }"></span>viewing</small>
                        </div>
                      </div>
                      <span v-else class="text-muted" style="font-size:.75rem;">—</span>
                    </td>
                    <td class="text-right">
                      <button class="btn btn-sm btn-view"
                        :class="{ 'btn-view-locked': isEmployee && isLockActive(diag) && !isMyLock(diag) }"
                        @click="openCode(diag)"
                        :disabled="isEmployee && isLockActive(diag) && !isMyLock(diag)"
                        :title="isEmployee && isLockActive(diag) && !isMyLock(diag) ? diag.locked_by_name + ' is viewing' : 'View'">
                        <i class="fe fe-lock mr-1" v-if="isEmployee && isLockActive(diag) && !isMyLock(diag)"></i>
                        <i class="fe fe-eye mr-1" v-else></i>View
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Grid View -->
          <div class="codes-grid" v-if="viewMode === 'grid'">
            <div v-if="!codes || !codes.data || !codes.data.length" style="grid-column:1/-1"
              class="text-center empty-state">
              <i class="fe fe-shield" style="font-size:2rem;opacity:0.3"></i>
              <p class="mt-2 mb-0">No authorization codes found</p>
            </div>
            <div v-for="diag in codes.data" :key="diag.id" class="code-grid-card">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <div class="font-weight-bold" style="font-size:.88rem;">
                    {{ diag.enrollee ? diag.enrollee.full_name : '—' }}
                  </div>
                  <small class="text-muted">{{ diag.provider ? diag.provider.agency_name : '—' }}</small>
                </div>
                <span :class="'status-badge ' + (diag.status || 'pending')">{{ diag.status || 'pending' }}</span>
              </div>
              <div class="d-flex align-items-center mb-2" style="gap:8px;">
                <code v-if="diag.code_created" class="code-chip">{{ diag.code_created }}</code>
                <span v-else class="text-muted small">Pending</span>
                <span v-if="diag.is_code_used" class="used-badge">used</span>
              </div>
              <div class="d-flex justify-content-between align-items-center grid-card-footer">
                <small class="text-muted">{{ diag.date_requested || '—' }}</small>
                <button class="btn btn-sm btn-view"
                  :class="{ 'btn-view-locked': isEmployee && isLockActive(diag) && !isMyLock(diag) }"
                  @click="openCode(diag)"
                  :disabled="isEmployee && isLockActive(diag) && !isMyLock(diag)">
                  <i class="fe fe-eye mr-1"></i>View
                </button>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div class="card-footer">
            <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 8px;">
              <small class="text-muted" v-if="rows > 0">
                Showing {{ (currentPage - 1) * perPage + 1 }}–{{ Math.min(currentPage * perPage, rows) }} of {{ rows }}
              </small>
              <b-pagination
                v-model="currentPage"
                :total-rows="rows"
                :per-page="perPage"
                aria-controls="codes-table"
                @input="getCodes(currentPage)"
                size="sm"
                class="mb-0"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- ── Employee Performance Report Modal ─────────────────── -->
      <div class="perf-modal-overlay" v-if="perfReport.open" @click.self="closePerformanceReport">
        <div class="card shadow perf-modal">
          <div class="d-flex align-items-center justify-content-between px-4 py-3 border-bottom">
            <div>
              <h6 class="mb-0 font-weight-bold">Employee Performance — Authorization Codes</h6>
              <small class="text-muted">{{ query.from || "All time" }} to {{ query.to || "now" }}</small>
            </div>
            <div class="d-flex align-items-center" style="gap: 8px;">
              <button
                class="btn btn-outline-success btn-sm"
                v-if="!perfReport.loading && perfReport.rows.length"
              >
                <download-excel :data="perfReport.rows" :fields="perf_fields" type="xls"
                  :escapeCsv="false" :name="'authcode_employee_performance.xls'">
                  <i class="fe fe-download mr-1"></i> Download Excel
                </download-excel>
              </button>
              <button type="button" class="btn btn-sm btn-outline-secondary" @click="closePerformanceReport">
                <i class="fe fe-x"></i>
              </button>
            </div>
          </div>
          <div class="px-4 py-3 perf-modal-body">
            <div v-if="perfReport.loading" class="text-center text-muted py-5">
              <i class="fe fe-loader"></i> Loading…
            </div>
            <div v-else-if="!perfReport.rows.length" class="text-center text-muted py-5">
              No codes approved in this date range
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Job Title</th>
                    <th class="text-right">Codes Approved</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in perfReport.rows" :key="row.user_id">
                    <td>{{ row.employee_name }}</td>
                    <td>{{ row.job_title || "—" }}</td>
                    <td class="text-right font-weight-bold">{{ row.count }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage" />
      </div>

    </main>
  </section>
</template>

<script>
  import Navbar from "@/views/Navbar.vue";
  import Loading from "vue-loading-overlay";
  import "vue-loading-overlay/dist/vue-loading.css";
  import { downloadServerExcel } from "@/service/serverExcel";
  import { enrolleeProgramme, forProgramme } from "@/utils/programmeCatalog";

  let agency = JSON.parse(localStorage.getItem("agency"));

  export default {
    components: { Navbar, Loading },
    data() {
      return {
        isLoading: false,
        fullPage: true,
        user: null,
        showadder: false,
        userDetails: false,
        diagnosisSearchTimeout: null,
        diagnosisError: false,
        servicesError: false,
        diseases: [],
        statuses: ["pending", "approved", "rejected"],
        exporting: false,
        query: {
          provider_id: "",
          // Facilities open on all their requests; agency staff on the pending queue.
          status: ["provider", "provider_employee"].includes((JSON.parse(localStorage.getItem("user")) || {}).type) ? "" : "pending",
          from: "",
          to: "",
          paginate_value: 30,
          code_created: "",
          id_card_number: "",
        },
        codes: { data: [], total: 0, per_page: 30 },
        currentPage: 1,
        rows: 0,
        perPage: 30,
        searchkey: "",
        selected_enrollee: {},
        referred_to_facility: "",
        provider_id: "",
        providers: [],
        search_result: [],
        service_summary_id: "",
        services: [],
        auth_services: [],
        diagnosis_id: "",
        encounters: [],
        comments: [{ body: "Code Request" }],
        register: { date_requested: "" },
        viewMode: "list",
        perfReport: { open: false, loading: false, rows: [] },
        perf_fields: {
          "Employee": "employee_name",
          "Job Title": "job_title",
          "Codes Approved": "count",
        },
      };
    },

    computed: {
      // Only services that apply to the enrollee's programme (utils/programmeCatalog).
      catalogProgramme() {
        return enrolleeProgramme(this.selected_enrollee);
      },
      serviceOptions() {
        return forProgramme(this.services, this.catalogProgramme);
      },
      isEmployee() {
        return this.user && this.user.type === "employee";
      },
      isFacility() {
        return !!this.user && (this.user.type === "provider" || this.user.type === "provider_employee");
      },
    },

    beforeMount() {
      this.user = JSON.parse(localStorage.getItem("user"));
    },

    mounted() {
      this.viewMode = window.innerWidth < 768 ? "grid" : "list";
    },

    methods: {
      openCode(diag) {
        const allIds = this.codes.data.map(d => d.id).join(",");
        const lockedIds = this.isEmployee
          ? this.codes.data.filter(d => this.isLockActive(d) && !this.isMyLock(d)).map(d => d.id).join(",")
          : "";
        const route = this.$router.resolve({
          path: `/authorization-code/${diag.id}`,
          query: { queue: allIds, locked: lockedIds || undefined },
        });
        window.open(route.href, "_blank");
      },

      isLockActive(diag) {
        if (!diag.locked_by_name) return false;
        if (!diag.locked_at) return true;
        return (Date.now() - new Date(diag.locked_at).getTime()) < 5 * 60 * 1000;
      },

      isMyLock(diag) {
        if (!diag.locked_by_name || !this.user) return false;
        if (diag.locked_by_user_id != null) return diag.locked_by_user_id === this.user.id;
        return diag.locked_by_name === `${this.user.firstname} ${this.user.lastname}`;
      },

      nameInitials(name) {
        if (!name) return "?";
        const parts = name.trim().split(" ");
        return (parts[0].charAt(0) + (parts[1] ? parts[1].charAt(0) : "")).toUpperCase();
      },

      clearFilter() {
        this.query = { provider_id: "", status: "", from: "", to: "", paginate_value: 30, code_created: "", id_card_number: "" };
        this.getCodes(1);
      },

      getCodes(currentPage) {
        this.currentPage = currentPage || 1;
        this.user = JSON.parse(localStorage.getItem("user"));
        if (this.user.type === "provider" || this.user.type === "provider_employee") {
          this.getCodesProvider(this.currentPage);
        } else {
          this.getCodesAgency(this.currentPage);
        }
      },

      // Filters shared by the list and the Excel export.
      agencyFilterParams() {
        return {
          org_id: this.user.type === "tpa_employee" ? this.user.institutional_id : null,
          provider_id: this.query.provider_id ? this.query.provider_id.id : null,
          status: this.query.status,
          id_card_number: this.query.id_card_number,
          code_created: this.query.code_created,
          from: this.query.from,
          to: this.query.to,
        };
      },

      getCodesAgency(currentPage) {
        this.currentPage = currentPage || 1;
        this.isLoading = true;
        this.axios
          .get(`/api/v1/auth/authorization_code/${agency.id}`, {
            params: {
              ...this.agencyFilterParams(),
              page: this.currentPage,
              paginate_value: this.query.paginate_value || this.perPage,
            },
          })
          .then((response) => {
            this.codes = response.data;
            this.rows = response.data.meta ? response.data.meta.total : (response.data.total || 0);
            this.perPage = response.data.meta ? response.data.meta.per_page : (response.data.per_page || 30);
            this.isLoading = false;
          })
          .catch((error) => { console.error(error); this.isLoading = false; });
      },

      // The API builds the workbook from every matching request (format=excel).
      async exportExcel() {
        this.exporting = true;
        try {
          await downloadServerExcel(this.axios, {
            method: "get",
            url: this.isFacility ? "/api/v1/auth/authorization_code-provider" : `/api/v1/auth/authorization_code/${agency.id}`,
            params: { ...(this.isFacility ? this.facilityFilterParams() : this.agencyFilterParams()), format: "excel" },
          }, `authorization_codes_${new Date().toISOString().slice(0, 10)}.xlsx`);
          this.$toasted.success("Excel file downloaded", { position: "top-center", duration: 3000 });
        } catch (error) {
          this.$toasted.error(error.message, { position: "top-center", duration: 4000 });
        } finally {
          this.exporting = false;
        }
      },

      // The API limits these to the signed-in facility's own requests.
      facilityFilterParams() {
        return {
          status: this.query.status,
          enrollee_search: this.query.id_card_number,
          code_created: this.query.code_created,
          from: this.query.from,
          to: this.query.to,
        };
      },

      getCodesProvider(currentPage) {
        this.currentPage = currentPage || 1;
        this.isLoading = true;
        this.axios
          .get(`/api/v1/auth/authorization_code-provider`, {
            params: {
              ...this.facilityFilterParams(),
              page: this.currentPage,
              paginate_value: this.query.paginate_value || this.perPage,
            },
          })
          .then((response) => {
            this.codes = response.data;
            this.rows = response.data.meta ? response.data.meta.total : (response.data.total || 0);
            this.perPage = response.data.meta ? response.data.meta.per_page : (response.data.per_page || 30);
            this.isLoading = false;
          })
          .catch((error) => { console.error(error); this.isLoading = false; });
      },

      searchIDCard() {
        this.isLoading = true;
        this.axios
          .post(`/api/v1/auth/searchenrollees`, {
            agency_id: agency.id,
            request_query: this.searchkey,
          })
          .then((response) => {
            this.search_result = response.data;
            this.userDetails = true;
            this.isLoading = false;
            this.$toasted.info("Search complete", { position: "top-center", duration: 2000 });
          })
          .catch((error) => { console.error(error); this.isLoading = false; });
      },

      AddDisease() {
        this.diagnosisError = false;
        this.servicesError = false;

        if (!this.diagnosis_id) this.diagnosisError = true;
        if (!this.auth_services || !this.auth_services.length) this.servicesError = true;
        if (this.diagnosisError || this.servicesError) {
          this.$toasted.error("Diagnosis and at least one service are required.", { position: "top-center", duration: 4000 });
          return;
        }

        this.isLoading = true;
        this.axios
          .post("/api/v1/auth/authorization_code", {
            agency_id: agency.id,
            principal_id: this.selected_enrollee.type === "client" ? this.selected_enrollee.id : 0,
            dependent_id: this.selected_enrollee.type === "Dependent" ? this.selected_enrollee.id : 0,
            provider_id: this.user.type === "provider_employee" ? this.user.institutional_id : this.provider_id,
            service_summary_id: this.service_summary_id,
            referred_to_facility: this.referred_to_facility ? this.referred_to_facility.id : null,
            org_id: this.selected_enrollee.org_id,
            services: this.auth_services,
            diagnosis_id: this.diagnosis_id ? this.diagnosis_id.id : null,
            comments: this.comments,
          })
          .then((response) => {
            this.isLoading = false;
            this.$toasted.success("Authorization code requested successfully!", { position: "top-center", duration: 3000 });
            const id = response.data.data.id;
            this.$router.push(`/authorization-code/${id}`);
          })
          .catch((error) => {
            console.log(error.response);
            this.isLoading = false;
            const message = error.response && error.response.data && error.response.data.message;
            this.$toasted.error(message || "Failed to submit request. Please try again.", { position: "top-center", duration: 6000 });
          });
      },

      getRecords(selected_enrollee) {
        this.axios
          .post(`/api/v1/auth/service_summary-agency`, {
            agency_id: agency.id,
            provider_id: this.user.institutional_id,
            client_id: selected_enrollee.type === "client" ? selected_enrollee.id : null,
            dependent_id: selected_enrollee.type === "Dependent" ? selected_enrollee.id : null,
          })
          .then((response) => { this.encounters = response.data.data; })
          .catch((error) => { console.error(error); });
      },

      getProviders() {
        this.axios
          .get(`/api/v1/auth/providerAgency/${agency.id}`)
          .then((response) => { this.providers = response.data.data; })
          .catch((error) => { console.error(error); });
      },

      getServices() {
        this.axios
          .get(`/api/v1/auth/service-agency/${agency.id}`)
          .then((response) => { this.services = response.data; })
          .catch((error) => { console.error(error); });
      },

      generateCode(diag) {
        this.axios
          .post(`/api/v1/auth/generateCode`, { id: diag.id })
          .then(() => {
            this.getCodes(this.currentPage);
            this.$toasted.info("Code generated successfully", { position: "top-center", duration: 3000 });
          })
          .catch((error) => { console.error(error); });
      },

      openPerformanceReport() {
        this.perfReport = { open: true, loading: true, rows: [] };
        this.axios
          .post("/api/v1/auth/authorization-code/statistics", {
            agency_id: agency.id,
            from: this.query.from || undefined,
            to: this.query.to || undefined,
          })
          .then(({ data }) => {
            this.perfReport.rows = (data.top_performers || []).map((row) => ({
              user_id: row.user_id,
              employee_name: row.name || `User ${row.user_id}`,
              job_title: row.job_title,
              count: row.count,
            }));
          })
          .catch((error) => {
            console.error(error);
            this.$toasted.error("Failed to load performance report.", { position: "top-center", duration: 3000 });
          })
          .finally(() => {
            this.perfReport.loading = false;
          });
      },

      closePerformanceReport() {
        this.perfReport.open = false;
      },

      onDiagnosisSearch(query, loading) {
        clearTimeout(this.diagnosisSearchTimeout);
        if (query.trim().length < 4) {
          if (query.trim().length === 0) this.diseases = [];
          return;
        }
        loading(true);
        this.diagnosisSearchTimeout = setTimeout(() => {
          const q = query.trim().toLowerCase();
          this.axios
            .post(`/api/v1/auth/diagnosis-search`, { name: q })
            .then((response) => {
              const all = Array.isArray(response.data) ? response.data : (response.data.data || []);
              this.diseases = all.filter(d => d.name && d.name.toLowerCase().includes(q));
              loading(false);
            })
            .catch((error) => { console.error(error); loading(false); });
        }, 400);
      },
    },

    created() {
      this.getCodes(1);
      this.getProviders();
      this.getServices();
    },
  };
</script>

<style scoped>
  .is-invalid-select .vs__dropdown-toggle {
    border-color: #dc3545 !important;
    box-shadow: 0 0 0 0.15rem rgba(220, 53, 69, .15);
  }

  /* ── Page Header ── */
  .page-header {
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    color: #fff;
  }
  .page-title { font-size: 1.6rem; font-weight: 700; margin: 0; color: #fff; }
  .page-subtitle { opacity: .75; margin: 4px 0 0; font-size: .9rem; }
  .mt-n3 { margin-top: -16px; }

  /* ── Buttons ── */
  .btn-create {
    background: linear-gradient(135deg, #2563eb, #7c3aed);
    color: #fff; border: none; border-radius: 8px;
    padding: 10px 22px; font-weight: 600;
  }
  .btn-create:hover { opacity: .9; color: #fff; }
  .btn-cancel {
    background: #f1f5f9; border: none; border-radius: 8px;
    padding: 10px 20px; color: #475569; font-weight: 600;
  }
  .btn-view {
    border-radius: 6px; border: 1px solid #e2e8f0;
    color: #1e3a5f; background: #fff; font-size: .8rem;
  }
  .btn-view:hover { background: #f1f5f9; }
  .btn-view-locked { border-color: #fca5a5 !important; color: #b91c1c !important; }
  .btn-view-locked:hover { background: #fef2f2 !important; }

  /* ── New Request Form Card ── */
  .form-card { border-radius: 14px; overflow: hidden; border: none; }
  .form-card-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 20px 28px;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
  }
  .form-close-btn {
    background: rgba(255,255,255,.15); border: none; border-radius: 8px;
    color: #fff; width: 34px; height: 34px; cursor: pointer; font-size: 1rem;
  }
  .form-close-btn:hover { background: rgba(255,255,255,.28); }

  /* ── Form Sections ── */
  .form-section { margin-bottom: 20px; padding-bottom: 20px; border-bottom: 1px solid #f1f5f9; }
  .form-section:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
  .form-section-title {
    display: flex; align-items: center; gap: 10px;
    font-weight: 600; color: #374151; font-size: .9rem; margin-bottom: 14px;
  }
  .section-number {
    width: 24px; height: 24px; border-radius: 50%;
    background: #2563eb; color: #fff;
    font-size: .75rem; font-weight: 700;
    display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .form-label { font-weight: 600; font-size: .85rem; color: #374151; margin-bottom: 6px; }
  .styled-select { border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 9px 12px; font-size: .88rem; }
  .styled-select:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.1); outline: none; }

  /* Patient preview */
  .patient-preview {
    display: flex; align-items: center; gap: 14px;
    background: #f0fdf4; border: 1px solid #bbf7d0;
    border-radius: 10px; padding: 12px 16px; margin-top: 8px;
  }
  .preview-avatar {
    width: 52px; height: 52px; border-radius: 50%;
    object-fit: cover; border: 2px solid #10b981; flex-shrink: 0;
  }

  /* ── Filter Card ── */
  .filter-card { border-radius: 12px; border: none; }
  .letter-spacing { letter-spacing: .05em; }

  /* ── Codes Table Card ── */
  .codes-card { border-radius: 12px; border: none; margin-bottom: 30px; }
  .card-header-custom {
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 24px; border-bottom: 1px solid #f1f5f9;
    background: #fff; border-radius: 12px 12px 0 0;
  }
  .card-header-custom h5 { margin: 0; font-weight: 600; color: #1e3a5f; }

  .codes-table { font-size: .9rem; }
  .codes-table thead th {
    background: #f8fafc; color: #64748b; font-weight: 600;
    font-size: .78rem; text-transform: uppercase; letter-spacing: .06em;
    border-bottom: 2px solid #e2e8f0; padding: 14px 20px;
  }
  .codes-table tbody td { padding: 14px 20px; vertical-align: middle; border-color: #f1f5f9; }
  .code-row:hover { background: #f8fafc; }

  .code-chip {
    background: #f1f5f9; color: #1e3a5f;
    padding: 3px 10px; border-radius: 6px;
    font-size: .82rem; font-weight: 600; letter-spacing: .04em;
  }

  /* Status badges */
  .status-badge {
    display: inline-block; padding: 4px 12px; border-radius: 20px;
    font-size: .78rem; font-weight: 600; text-transform: capitalize;
  }
  .status-badge.pending  { background: #fef3c7; color: #92400e; }
  .status-badge.approved { background: #d1fae5; color: #065f46; }
  .status-badge.rejected { background: #fee2e2; color: #991b1b; }
  .used-badge {
    display: inline-block; background: #d1fae5; color: #065f46;
    font-size: .72rem; font-weight: 700; padding: 2px 8px; border-radius: 20px;
  }

  .empty-state { padding: 40px !important; color: #94a3b8; }

  .card-footer {
    background: #fff; border-top: 1px solid #f1f5f9;
    border-radius: 0 0 12px 12px; padding: 12px 24px;
  }

  /* ── Live lock badge ── */
  .live-lock-badge { display: flex; align-items: center; gap: 7px; }
  .live-avatar {
    width: 28px; height: 28px; border-radius: 50%;
    background: linear-gradient(135deg, #f59e0b, #ef4444);
    color: #fff; font-size: .68rem; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; box-shadow: 0 0 0 2px rgba(239,68,68,.25);
  }
  .live-avatar-mine {
    background: linear-gradient(135deg, #1e3a5f, #2563eb) !important;
    box-shadow: 0 0 0 2px rgba(37,99,235,.25) !important;
  }
  .live-info { display: flex; flex-direction: column; line-height: 1.25; }
  .live-info span { font-size: .78rem; font-weight: 600; color: #374151; white-space: nowrap; }
  .live-info small { font-size: .7rem; color: #ef4444; display: flex; align-items: center; gap: 4px; }
  .live-dot {
    display: inline-block; width: 6px; height: 6px; border-radius: 50%;
    background: #ef4444; animation: live-pulse 1.2s ease-in-out infinite;
  }
  .live-dot-mine { background: #2563eb !important; }
  @keyframes live-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: .4; transform: scale(.8); }
  }

  /* ── View toggle ── */
  .view-toggle { display: flex; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
  .view-toggle-btn {
    background: none; border: none; padding: 6px 10px;
    color: #94a3b8; cursor: pointer; font-size: .85rem; line-height: 1;
  }
  .view-toggle-btn.active { background: #1e3a5f; color: #fff; }
  .view-toggle-btn:hover:not(.active) { background: #f1f5f9; color: #475569; }

  /* ── Grid view ── */
  .codes-grid {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
    gap: 16px; padding: 20px 24px;
  }
  .code-grid-card {
    border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px;
    background: #fff; display: flex; flex-direction: column;
    transition: border-color .15s, box-shadow .15s;
  }
  .code-grid-card:hover { border-color: #93c5fd; box-shadow: 0 2px 10px rgba(37,99,235,.1); }
  .grid-card-footer { border-top: 1px solid #f1f5f9; padding-top: 10px; margin-top: auto; }

  /* ── Employee Performance Modal ── */
  .perf-modal-overlay {
    position: fixed; inset: 0; z-index: 1050;
    background: rgba(0,0,0,0.5);
    display: flex; align-items: center; justify-content: center; padding: 24px;
  }
  .perf-modal { width: 100%; max-width: 640px; max-height: 85vh; display: flex; flex-direction: column; }
  .perf-modal-body { overflow-y: auto; }
</style>
